import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ActivityDto, NoteDto } from '../../shared/api';
import { GONE, NUMCATS, QC_INTERVALS, REPAIR, typeLabel } from '../../shared/domain';
import { get, patch, post, readPhoto, refresh } from '../api';
import { Bar, Err, QcTag, Spinner, Tag, Where, errMsg, hm, useApp, ymd } from './ui';

export function Dialog({ title, onClose, children }: { title: ReactNode; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (!ref.current?.open) ref.current?.showModal();
  }, []);
  return (
    <dialog ref={ref} onClose={onClose} onCancel={onClose}>
      <div className="mhead">
        <strong>{title}</strong>
        <button className="btn small" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="mbody">{children}</div>
    </dialog>
  );
}

/** Run an API call, show its message, refresh the data. */
function useRun() {
  const { toast } = useApp();
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<{ text?: string } | unknown>, okMsg?: string) => {
    setErr('');
    setBusy(true);
    try {
      const r = (await fn()) as { text?: string } | null;
      await refresh();
      const m = r?.text ?? okMsg;
      if (m) toast(m);
      return true;
    } catch (e) {
      setErr(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { run, err, busy, setErr };
}

export function ItemCard({ no }: { no: string }) {
  const { d, closeDialog, openArticle, startAction } = useApp();
  const i = d.item(no);
  const detail = useQuery({
    queryKey: ['item', no],
    queryFn: () => get<{ notes: NoteDto[]; history: ActivityDto[] }>(`/api/items/${encodeURIComponent(no)}/detail`),
  });
  const { run, err, busy } = useRun();
  const [qcNote, setQcNote] = useState('');
  const [note, setNote] = useState('');
  const [gone, setGone] = useState<{ reason: string; what: string } | null>(null);
  if (!i) return <Dialog title={no} onClose={closeDialog}>This item is not in the app.</Dialog>;

  const a = d.artOf(i);
  const q = d.qcOf(i);
  const own = d.isOwner;
  const inR1 = i.status === 'in' && d.isRepair(d.shelf(i.loc?.shelf));
  const locked = a.type === '2-1' && !own;
  const url = `/api/items/${encodeURIComponent(i.no)}`;
  const start = (action: 'register' | 'take' | 'move') => {
    closeDialog();
    startAction({ action, no: i.no });
  };

  return (
    <Dialog title={<span className="code">{i.no}</span>} onClose={closeDialog}>
      <div className="row" style={{ alignItems: 'flex-start', gap: 16 }}>
        <div style={{ width: 120 }}>
          {a.photo ? <img className="kpic" src={a.photo} alt="" /> : <div className="nopic" style={{ borderColor: 'var(--line)' }}>No photo</div>}
        </div>
        <dl className="kv" style={{ flex: 1, minWidth: 240 }}>
          <dt>Article</dt>
          <dd>
            <button className="linkbtn" onClick={() => openArticle(a.id)}>
              <b>{a.name}</b>
            </button>
            <br />
            <span className="meta">{typeLabel(a)}</span>
          </dd>
          <dt>Number group</dt>
          <dd>
            {i.cc} {NUMCATS[i.cc]?.name ?? ''}, purchase year 20{i.year}
          </dd>
          {i.proj && (
            <>
              <dt>Bought for</dt>
              <dd>
                {d.projTxt(i.proj)} <span className="meta">(from the number)</span>
              </dd>
              <dt>Now used for</dt>
              <dd>
                {own ? (
                  <select value={d.usedFor(i)} onChange={(e) => void run(() => patch(url, { nowFor: e.target.value }), 'Saved')}>
                    {d.b.projects.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.code} {p.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  d.projTxt(d.usedFor(i))
                )}
                {d.reserved(i) && (
                  <>
                    {' '}
                    <Tag cls="soft">reserved</Tag>
                  </>
                )}
              </dd>
            </>
          )}
          <dt>Where now</dt>
          <dd>
            <Where i={i} />
          </dd>
          <dt>Home area</dt>
          <dd>
            <span className="code">{d.homeOf(i)}</span>
          </dd>
          {a.stock ? (
            <>
              <dt>In this box</dt>
              <dd>{d.fmtQty(i)}</dd>
            </>
          ) : (
            <>
              <dt>Serial number</dt>
              <dd>
                {own ? (
                  <input defaultValue={i.serial} placeholder="none" onBlur={(e) => e.target.value.trim() !== i.serial && void run(() => patch(url, { serial: e.target.value }), 'Saved')} />
                ) : (
                  i.serial || <span className="meta">none</span>
                )}{' '}
                <span className="meta">extra info, not used for tracking</span>
              </dd>
            </>
          )}
          {a.type === '1-1' && (
            <>
              <dt>Next tool check</dt>
              <dd>
                {i.checkDue ?? <span className="meta">not set</span>}{' '}
                <button className="btn small" disabled={busy} onClick={() => void run(() => post(`${url}/tool-check`), 'Tool check saved')}>
                  Check done
                </button>
              </dd>
            </>
          )}
        </dl>
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        {i.status === 'gone' ? (
          <button className="btn" onClick={() => start('register')}>
            Found again, register on a shelf
          </button>
        ) : locked ? (
          <span className="meta">Products in L1: only the warehouse owner can take them out or move them.</span>
        ) : i.status === 'in' ? (
          <>
            <button className="btn" onClick={() => start('take')}>
              Take out
            </button>
            <button className="btn" onClick={() => start('move')}>
              Move
            </button>
          </>
        ) : (
          <button className="btn" onClick={() => start('register')}>
            Register back on a shelf
          </button>
        )}
        {i.status !== 'gone' && !locked && !gone && (
          <select value="" onChange={(e) => e.target.value && setGone({ reason: e.target.value, what: '' })}>
            <option value="">Mark as lost or thrown away…</option>
            {GONE.map((g) => (
              <option key={g}>{g}</option>
            ))}
          </select>
        )}
      </div>
      {gone && (
        <div className="box" style={{ borderColor: 'var(--bad)' }}>
          <label className="field">
            {gone.reason}: what happened? (required)
            <textarea value={gone.what} onChange={(e) => setGone({ ...gone, what: e.target.value })} />
          </label>
          <div className="row" style={{ marginTop: 8 }}>
            <button className="btn primary" disabled={busy || !gone.what.trim()} onClick={async () => (await run(() => post(`${url}/gone`, gone), `Marked as ${gone.reason.toLowerCase()}`)) && setGone(null)}>
              Save. The number is kept.
            </button>
            <button className="btn" onClick={() => setGone(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {inR1 && (
        <div className="box" style={{ borderColor: 'var(--warn)' }}>
          <h3>In the repair / unused corner (R1) since {i.repairSince ?? '?'}</h3>
          <div className="row">
            <label className="field">
              Status
              <select value={i.repair ?? REPAIR[0]} onChange={(e) => void run(() => patch(url, { repair: e.target.value }), 'Saved')}>
                {REPAIR.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </label>
            {!locked && (
              <button
                className="btn primary"
                style={{ alignSelf: 'flex-end' }}
                onClick={() => {
                  closeDialog();
                  startAction({ action: 'move', no: i.no, toShelf: d.homeOf(i), comment: 'Fixed, back to its home area.' });
                }}
              >
                Fixed, send back to {d.homeOf(i)}
              </button>
            )}
          </div>
        </div>
      )}

      {i.status !== 'gone' && (
        <div className="box">
          <h3>Quality check (QC)</h3>
          {a.qcNeeded ? (
            <>
              <div className="qcbig">
                <div className={`tick ${q.cls === 'ok' ? 'ok' : q.cls === 'warn' ? 'warn' : 'bad'}`}>{q.ok ? '✓' : '!'}</div>
                <div>
                  <b>{q.t}</b>
                  <br />
                  <span className="meta">
                    {i.qc ? `Last QC ${i.qc.last} by ${i.qc.by}, result: ${i.qc.result}.` : 'No QC saved yet.'} QC every {a.qcEvery} months (set on the article).
                  </span>
                </div>
              </div>
              <label className="field" style={{ marginTop: 12 }}>
                QC note
                <textarea value={qcNote} onChange={(e) => setQcNote(e.target.value)} placeholder="What did you check? If it failed, what is wrong?" />
              </label>
              <div className="row" style={{ marginTop: 8 }}>
                <button className="btn primary" disabled={busy} onClick={async () => (await run(() => post(`${url}/qc`, { result: 'Pass', note: qcNote }))) && setQcNote('')}>
                  ✓ Save QC passed
                </button>
                <button className="btn" disabled={busy} onClick={async () => (await run(() => post(`${url}/qc`, { result: 'Fail', note: qcNote }))) && setQcNote('')}>
                  Save QC failed
                </button>
              </div>
            </>
          ) : (
            <p className="meta" style={{ margin: 0 }}>
              This article does not need QC.
            </p>
          )}
        </div>
      )}
      <Err e={err} />

      <div className="box">
        <h3>Notes and QC history ({detail.data?.notes.length ?? '…'})</h3>
        <p className="meta" style={{ margin: '0 0 8px' }}>
          Every user can add a note. Notes keep the name, date and time, and cannot be changed or deleted.
        </p>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Second year in storage, QC planned for next week" />
        <div className="row" style={{ marginTop: 6 }}>
          <button className="btn" disabled={busy || !note.trim()} onClick={async () => (await run(() => post(`${url}/notes`, { text: note }), 'Note added')) && setNote('')}>
            Add note
          </button>
        </div>
        {detail.isLoading ? (
          <Spinner />
        ) : detail.data?.notes.length ? (
          detail.data.notes.map((n, k) => (
            <div key={k} className={`note ${n.kind}`}>
              <span className="meta">
                {ymd(n.at)}, {hm(n.at)} · {n.by} · {n.kind === 'qc' ? 'QC passed' : n.kind === 'qcf' ? 'QC failed' : 'Note'}
              </span>
              {n.text}
            </div>
          ))
        ) : (
          <p className="meta">No notes yet.</p>
        )}
      </div>
      <div className="box">
        <h3>History of this item ({detail.data?.history.length ?? '…'})</h3>
        {detail.data?.history.length ? (
          detail.data.history.map((x) => (
            <div key={x.id} className="note">
              <span className="meta">
                {ymd(x.at)}, {hm(x.at)} · {x.user} · {x.action}
              </span>
              {x.desc}
            </div>
          ))
        ) : (
          <p className="meta">No activities yet.</p>
        )}
      </div>
    </Dialog>
  );
}

export function ArticleCard({ id }: { id: number }) {
  const { d, closeDialog, openItem } = useApp();
  const a = d.art(id);
  const { run, err, busy, setErr } = useRun();
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ specs: '', supplier: '', price: '', link: '', expiry: '' });
  if (!a) return <Dialog title="Article" onClose={closeDialog}>Not found.</Dialog>;
  const own = d.isOwner;
  const its = d.b.items.filter((i) => i.articleId === id);
  const st = d.artStock(a);
  const url = `/api/articles/${id}`;
  const upd = (body: object) => run(() => patch(url, body), 'Saved');

  if (edit)
    return (
      <Dialog title={`Edit article: ${a.name}`} onClose={closeDialog}>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <label className="field" style={{ flex: 2, minWidth: 220 }}>
            Specifications
            <input value={f.specs} onChange={(e) => setF({ ...f, specs: e.target.value })} />
          </label>
          <label className="field">
            Supplier
            <input value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} />
          </label>
          <label className="field" style={{ width: 100 }}>
            Price €
            <input type="number" step="any" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
          </label>
        </div>
        <div className="row" style={{ alignItems: 'flex-end', marginTop: 10 }}>
          <label className="field" style={{ flex: 2, minWidth: 240 }}>
            Purchase link
            <input value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} />
          </label>
          {a.type === '2-3' && (
            <label className="field">
              Expiry date
              <input type="date" value={f.expiry} onChange={(e) => setF({ ...f, expiry: e.target.value })} />
            </label>
          )}
        </div>
        <Err e={err} />
        <div className="row" style={{ marginTop: 14 }}>
          <button
            className="btn primary"
            disabled={busy}
            onClick={async () => {
              const ok = await upd({ specs: f.specs, supplier: f.supplier, price: parseFloat(f.price) || 0, link: f.link, ...(a.type === '2-3' ? { expiry: f.expiry || null } : {}) });
              if (ok) setEdit(false);
            }}
          >
            Save
          </button>
          <button className="btn" onClick={() => setEdit(false)}>
            Back
          </button>
        </div>
      </Dialog>
    );

  return (
    <Dialog title={a.name} onClose={closeDialog}>
      <div className="row" style={{ alignItems: 'flex-start', gap: 16 }}>
        <div style={{ width: 130 }}>
          {a.photo ? <img className="kpic" src={a.photo} alt="" /> : <div className="nopic" style={{ borderColor: 'var(--line)' }}>No photo</div>}
          <label className="btn small" style={{ display: 'block', textAlign: 'center', marginTop: 6 }}>
            {a.photo ? 'Change photo' : 'Add photo'}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const photo = await readPhoto(file);
                  await run(() => post(`${url}/photo`, { photo }), 'Photo saved');
                } catch (x) {
                  setErr(errMsg(x));
                }
              }}
            />
          </label>
        </div>
        <dl className="kv" style={{ flex: 1, minWidth: 240 }}>
          <dt>Storage type</dt>
          <dd>
            {typeLabel(a)}, home {d.homeOfArt(a)}
          </dd>
          <dt>Counted</dt>
          <dd>{a.stock ? `per box, in ${a.unit}` : 'one number per piece'}</dd>
          <dt>In stock</dt>
          <dd>
            {a.stock ? `${+st.toFixed(2)} ${a.unit}` : `${its.filter((i) => i.status === 'in').length} pc`}
            {a.stock && <Bar q={st} min={a.min} />}
          </dd>
          {a.stock && (
            <>
              <dt>Minimum</dt>
              <dd>
                {own ? (
                  <>
                    <input type="number" min={0} step="any" defaultValue={a.min} style={{ width: 100 }} onBlur={(e) => Number(e.target.value) !== a.min && void upd({ min: parseFloat(e.target.value) || 0 })} /> {a.unit}
                  </>
                ) : a.min ? (
                  `${a.min} ${a.unit}`
                ) : (
                  <Tag cls="warn">not set</Tag>
                )}
                {d.artLow(a) && (
                  <>
                    {' '}
                    <Tag cls="bad">reorder</Tag>
                  </>
                )}
              </dd>
            </>
          )}
          {a.qcNeeded && (
            <>
              <dt>QC every</dt>
              <dd>
                {own ? (
                  <select value={a.qcEvery} onChange={(e) => void upd({ qcEvery: Number(e.target.value) })}>
                    {QC_INTERVALS.map((m) => (
                      <option key={m} value={m}>
                        {m} months
                      </option>
                    ))}
                  </select>
                ) : (
                  `${a.qcEvery} months`
                )}
              </dd>
            </>
          )}
          {a.expiry && (
            <>
              <dt>Expires</dt>
              <dd>{a.expiry}</dd>
            </>
          )}
        </dl>
      </div>
      <div className="box">
        <h3>Size, details and supplier</h3>
        <dl className="kv">
          <dt>Specifications</dt>
          <dd>{a.specs || <span className="meta">none</span>}</dd>
          <dt>Supplier</dt>
          <dd>{a.supplier || <span className="meta">none</span>}</dd>
          <dt>Price</dt>
          <dd>{a.price ? `€${a.price}` : <span className="meta">none</span>}</dd>
          <dt>Purchase link</dt>
          <dd>
            {a.link ? (
              <>
                {a.linkExpired && (
                  <>
                    <Tag cls="bad">No longer works</Tag>{' '}
                  </>
                )}
                <a href={a.link} target="_blank" rel="noopener noreferrer">
                  {a.link}
                </a>
              </>
            ) : (
              <span className="meta">none</span>
            )}
          </dd>
        </dl>
        <div className="row" style={{ marginTop: 10 }}>
          {a.link && (
            <button className="btn small" disabled={busy} onClick={() => void upd({ linkExpired: !a.linkExpired })}>
              {a.linkExpired ? 'Link works again' : 'Link does not work'}
            </button>
          )}
          <a className="btn small" href={`https://www.google.com/search?q=${encodeURIComponent(a.name + ' ' + a.specs)}`} target="_blank" rel="noopener noreferrer">
            Find similar
          </a>
          {own && (
            <button
              className="btn small"
              onClick={() => {
                setF({ specs: a.specs, supplier: a.supplier, price: a.price ? String(a.price) : '', link: a.link, expiry: a.expiry ?? '' });
                setEdit(true);
              }}
            >
              Edit info
            </button>
          )}
        </div>
        {own && a.type !== '2-1' && (
          <label className="row meta" style={{ gap: 6, marginTop: 10 }}>
            <input type="checkbox" checked={a.qcNeeded} onChange={(e) => void upd({ qcNeeded: e.target.checked })} /> QC is needed for this article
          </label>
        )}
        <Err e={err} />
      </div>
      <div className="box">
        <h3>All items of this article ({its.length})</h3>
        <div className="scroll">
          <table style={{ minWidth: 520 }}>
            <thead>
              <tr>
                <th>Number</th>
                <th>Where</th>
                <th>Bought for / used for</th>
                {a.stock && <th>Qty</th>}
              </tr>
            </thead>
            <tbody>
              {its.map((i) => (
                <tr key={i.no} className="click" onClick={() => openItem(i.no)}>
                  <td className="code" style={{ whiteSpace: 'nowrap' }}>
                    {i.no}
                  </td>
                  <td>
                    <Where i={i} />
                  </td>
                  <td>{i.proj ? i.proj + (d.usedFor(i) !== i.proj ? ' → ' + d.usedFor(i) : '') : <span className="meta">—</span>}</td>
                  {a.stock && <td>{d.fmtQty(i)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Dialog>
  );
}

export { QcTag };
